/**
 * Nira Example
 *
 * This module demonstrates how to use the NiraManager with custom camera positions
 * and adds an interactive circle to the map.
 */

import { getNiraManager } from './niraManager.js';
import { getMapManager } from '../components/Map/MapManager.js';
import { getCurrentCameraPosition } from './mapCameraUtils.js';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl';

/**
 * Launch Nira with the default hardcoded camera position
 * @deprecated Use launchNiraViewer() or niraManager.launchWithPosition() instead
 */
export function launchNira() {
    console.log('[NiraExample] DEPRECATED: launchNira called, use launchNiraViewer or niraManager.launchWithPosition instead');
    const niraManager = getNiraManager();
    if (!niraManager) {
        console.error('[NiraExample] Nira manager not initialized');
        return;
    }
    
    // Launch with position instead of the legacy launch method
    niraManager.launchWithPosition(NiraViewpoints.prophetsMosque);
}

/**
 * Launch Nira viewer with the standard Smart Transportation Hub position
 * This is a convenience function that delegates to the standard niraManager.launchWithPosition
 */
export function launchNiraViewer() {
    console.log('[NiraExample] launchNiraViewer called, using standard position');
    const niraManager = getNiraManager();
    if (!niraManager) {
        console.error('[NiraExample] Nira manager not initialized');
        return;
    }
    
    // Use the standard position from NiraViewpoints
    const position = NiraViewpoints.prophetsMosque;
    
    // Launch using the standard method from niraManager
    niraManager.launchWithPosition(position);
}

/**
 * Function to get project coordinates from city data
 * @param {string} cityId - The city ID to search in
 * @param {string} projectName - The project name to search for
 * @returns {Array|null} - [lng, lat] coordinates or null if not found
 */
function getProjectCoordinates(cityId, projectName) {
    const panel = document.querySelector(`.panel[data-city-id="${cityId}"]`);
    if (!panel) {
        console.warn(`[NiraExample] No panel found for city: ${cityId}`);
        return null;
    }
    
    try {
        const projectsData = JSON.parse(panel.dataset.projects || '[]');
        const project = projectsData.find(p => p.project_name === projectName);
        
        if (project && project.longitude && project.latitude) {
            return [project.longitude, project.latitude];
        }
    } catch (error) {
        console.error(`[NiraExample] Error parsing projects data for ${cityId}:`, error);
    }
    
    return null;
}

/**
 * Example of camera positions for specific views
 * These are dynamically generated when accessed
 */
export const NiraViewpoints = {
    // Prophet's Mosque in Madinah (Smart Transportation Hub)
    get prophetsMosque() {
        // Try to get coordinates from project data
        const coordinates = getProjectCoordinates('madinah', 'Smart Transportation Hub');
        
        return {
            center: coordinates || [39.611136, 24.468238], // Fallback to hardcoded if not found
            zoom: 14.71,
            pitch: 62.27,
            bearing: 0.00
        };
    },
    
    // Quba Mosque in Madinah
    get qubaMosque() {
        return {
            center: [39.619500, 24.435278], // This is a specific location, not a project
            zoom: 15.20,
            pitch: 60.00,
            bearing: 30.00
        };
    },
    
    // Example of how to use these viewpoints:
    //
    // import { launchNiraWithPosition, NiraViewpoints } from './features/niraExample.js';
    //
    // // Launch Nira with the Prophet's Mosque viewpoint
    // launchNiraWithPosition(NiraViewpoints.prophetsMosque);
    //
    // // Launch Nira with the Quba Mosque viewpoint
    // launchNiraWithPosition(NiraViewpoints.qubaMosque);
}

// Store the Nira source and layer IDs
const NIRA_SOURCE_ID = 'nira-center-source';
const NIRA_CIRCLE_SOURCE_ID = 'nira-circle-source';
const NIRA_LAYER_ID = 'nira-center-layer';
const NIRA_CIRCLE_LAYER_ID = 'nira-circle-layer';

// Store the Nira project coordinates globally
let niraProjectCoordinates = [39.611136, 24.468238]; // Default: Smart Transportation Hub coordinates

/**
 * Find the Nira project coordinates from the panels
 * @returns {Array} [lng, lat] coordinates
 */
function findNiraProjectCoordinates() {
    const panels = document.querySelectorAll('.panel');
    
    for (const panel of panels) {
        const cityId = panel.dataset.cityId;
        if (cityId === 'madinah') {
            try {
                const projectsData = JSON.parse(panel.dataset.projects || '[]');
                const niraProject = projectsData.find(project => project.launch_nira === true);
                
                if (niraProject && niraProject.longitude && niraProject.latitude) {
                    return [niraProject.longitude, niraProject.latitude];
                }
            } catch (error) {
                console.error('[NiraExample] Error parsing projects data:', error);
            }
        }
    }
    
    console.warn('[NiraExample] No project with launch_nira found, using default coordinates');
    return [39.611136, 24.468238]; // Smart Transportation Hub coordinates
}

/**
 * Add an interactive circle to the map that launches Nira when clicked
 * This uses GeoJSON sources and layers with turf.js for the circle
 */
export function addNiraInteractiveCircle() {
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[NiraExample] Map not initialized');
        return;
    }
    
    const map = mapManager.getMap();
    
    // Find project coordinates
    niraProjectCoordinates = findNiraProjectCoordinates();
    console.log('[NiraExample] Using Nira project coordinates:', niraProjectCoordinates);
    
    // Create a point feature for the center
    const centerPoint = turf.point(niraProjectCoordinates);
    
    // Create a circle feature with a 500m radius
    const circleFeature = turf.circle(centerPoint, 3.1, { units: 'kilometers' });
    
    // Extract just the perimeter coordinates from the polygon
    const perimeterCoordinates = circleFeature.geometry.coordinates[0];
    
    // Create a Polygon for the circle (with fill)
    const ringFeature = {
        type: 'Feature',
        properties: {},
        geometry: {
            type: 'Polygon',
            coordinates: [perimeterCoordinates] // Wrap in array for Polygon
        }
    };
    
    try {
        // Add sources - NO CHECKS, exactly like the Mapbox example
        map.addSource(NIRA_SOURCE_ID, {
            type: 'geojson',
            data: centerPoint
        });
        
        map.addSource(NIRA_CIRCLE_SOURCE_ID, {
            type: 'geojson',
            data: ringFeature
        });
        
        // Add fill layer first (bottom layer)
        map.addLayer({
            id: NIRA_CIRCLE_LAYER_ID,
            type: 'fill',
            source: NIRA_CIRCLE_SOURCE_ID,
            paint: {
                'fill-color': '#ffffff',
                'fill-opacity': 0.1
            }
        });

        // Add outline layer
        map.addLayer({
            id: NIRA_CIRCLE_LAYER_ID + '-outline',
            type: 'line',
            source: NIRA_CIRCLE_SOURCE_ID,
            paint: {
                'line-color': '#ffffff',
                'line-width': 2
            }
        });

        // Add center point layer (top layer)
        map.addLayer({
            id: NIRA_LAYER_ID,
            type: 'circle',
            source: NIRA_SOURCE_ID,
            paint: {
                'circle-radius': 8,
                'circle-color': '#ffffff'
            }
        });
        
        // Remove any existing event listeners from all layers
        [NIRA_LAYER_ID, NIRA_CIRCLE_LAYER_ID, NIRA_CIRCLE_LAYER_ID + '-outline'].forEach(id => {
            map.off('click', id);
            map.off('mouseenter', id);
            map.off('mouseleave', id);
        });

        // Add click and hover events for fill layer only
        const handleNiraClick = () => {
            console.log('[NiraExample] Nira circle clicked');
            launchNiraViewer();
        };

        const handleMouseEnter = () => {
            map.getCanvas().style.cursor = 'pointer';
        };
        
        const handleMouseLeave = () => {
            map.getCanvas().style.cursor = '';
        };

        // Add event handlers to fill layer only
        map.on('click', NIRA_CIRCLE_LAYER_ID, handleNiraClick);
        map.on('mouseenter', NIRA_CIRCLE_LAYER_ID, handleMouseEnter);
        map.on('mouseleave', NIRA_CIRCLE_LAYER_ID, handleMouseLeave);
        
        console.log('[NiraExample] Added/updated Nira interactive circle at', niraProjectCoordinates);
    } catch (error) {
        console.error('[NiraExample] Error adding/updating Nira circle:', error);
    }
    
    // Event handlers are now added in the conditional blocks above
    // when the layers are first created
}

/**
 * Properly remove Nira interactive circle from the map
 * This is useful when explicitly needing to remove the circle
 * before adding it again or before style changes
 */
export function removeNiraInteractiveCircle() {
    console.log('[NiraExample] Removing Nira interactive circle');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[NiraExample] Map not initialized');
        return;
    }
    
    const map = mapManager.getMap();
    
    // Remove layers and sources in the correct order
    try {
        // Remove all layers first
        const layerIds = [NIRA_LAYER_ID, NIRA_CIRCLE_LAYER_ID, NIRA_CIRCLE_LAYER_ID + '-outline'];
        layerIds.forEach(id => {
            if (map.getLayer(id)) {
                map.removeLayer(id);
            }
        });
        
        // Then remove sources
        const sourceIds = [NIRA_SOURCE_ID, NIRA_CIRCLE_SOURCE_ID];
        sourceIds.forEach(id => {
            if (map.getSource(id)) {
                map.removeSource(id);
            }
        });
        
        console.log('[NiraExample] Successfully removed Nira circle elements');
    } catch (error) {
        console.error('[NiraExample] Error removing Nira circle:', error);
    }
}

// Expose the functions to the window object for use in MapManager
if (typeof window !== 'undefined') {
    window.addNiraInteractiveCircle = addNiraInteractiveCircle;
    window.removeNiraInteractiveCircle = removeNiraInteractiveCircle;
}